import { faCheck, faCopy } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useCallback, useEffect, useState } from "react";

import { cn } from "../../utils/cn";
import { Button } from "../button/Button";
import { Tooltip } from "../tooltip/Tooltip";
import { Text } from "./Text";

import type { HTMLAttributes, ReactNode } from "react";
import type { VariantProps } from "tailwind-variants";
import type { textVariants } from "./Text";

export interface CopyableTextProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof textVariants> {
  /** The text content to display. If not provided, only the copy button is shown. */
  children?: string;
  /** Size of the copy button */
  copyButtonSize?: "xs" | "sm" | "md" | "lg";
  /** Duration of the copy state in milliseconds */
  copyDuration?: number;
  /** Custom text to display when copied */
  copiedText?: string;
  /** Whether to truncate the text */
  truncate?: boolean;
  /** Custom tooltip text for the copy button */
  copyTooltip?: string;
  /**
   * Text to copy to clipboard. If not provided, uses children.
   * antd compatibility: allows copying different text than what's displayed.
   */
  textToCopy?: string;
  /**
   * Custom icon to display for the copy button.
   * antd compatibility: replaces the default copy icon.
   */
  icon?: ReactNode;
  /**
   * Tuple of tooltips: [beforeCopy, afterCopy].
   * antd compatibility: alternative to copyTooltip + copiedText.
   */
  tooltips?: [string, string];
  /**
   * Callback fired after text is copied to clipboard.
   * antd compatibility: onCopy callback.
   */
  onCopy?: () => void;
}

export const CopyableText = forwardRef<HTMLDivElement, CopyableTextProps>(
  (
    {
      children,
      copyButtonSize = "xs",
      copyDuration = 1000,
      copiedText: copiedTextProp,
      truncate = true,
      copyTooltip: copyTooltipProp,
      textToCopy,
      icon,
      tooltips,
      onCopy,
      className,
      size,
      bold,
      emphasis,
      ...props
    },
    ref
  ) => {
    const [isCopied, setIsCopied] = useState(false);

    // Use textToCopy if provided, otherwise use children
    const textToClipboard = textToCopy ?? children ?? "";

    // Support antd's tooltips array or individual props
    const beforeCopyTooltip = tooltips?.[0] ?? copyTooltipProp ?? "Copy";
    const afterCopyTooltip = tooltips?.[1] ?? copiedTextProp ?? "Copied!";

    const handleCopyClick = useCallback(async () => {
      try {
        await navigator.clipboard.writeText(textToClipboard);
        setIsCopied(true);
        onCopy?.();
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    }, [textToClipboard, onCopy]);

    useEffect(() => {
      if (!isCopied) return;
      const timeout = setTimeout(() => setIsCopied(false), copyDuration);
      return () => clearTimeout(timeout);
    }, [copyDuration, isCopied]);

    // Determine if we should show the text content
    const showTextContent = children !== undefined && children !== "";

    return (
      <div ref={ref} {...props} className={cn("flex min-w-0 items-center gap-x-1", className)}>
        {showTextContent && (
          <div className={cn("min-w-0", truncate && "truncate")}>
            <Text size={size} bold={bold} emphasis={emphasis}>
              {children}
            </Text>
          </div>
        )}
        <div className="shrink-0">
          {isCopied ? (
            <>
              <Text emphasis="secondary">{afterCopyTooltip}</Text>
              <FontAwesomeIcon className="pl-1 text-secondary" icon={faCheck} />
            </>
          ) : (
            <Tooltip content={beforeCopyTooltip}>
              <Button
                isIconOnly
                size={copyButtonSize}
                variant="neutralLink"
                onClick={handleCopyClick}
                aria-label={`Copy ${textToClipboard}`}
              >
                {icon ?? <FontAwesomeIcon icon={faCopy} />}
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }
);

CopyableText.displayName = "CopyableText";
